/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import PatchManager from "App/utils/globe/chunk_lod/patch_manager";
import Visitor, {VisitorOrdering} from "App/utils/globe/chunk_lod/visitor";
import DownloadQuery, {eDownloadQueryState} from "App/utils/globe/chunk_lod/network/download_query";
import {vec3} from "gl-matrix";
import {ChunkLODNodeState} from "App/utils/globe/chunk_lod/tree/chunk_lod_node";
import TreeNode from "App/utils/globe/chunk_lod/tree/node";

export default
class DownloadManager extends Visitor {
    private _freeDownloadSlots: number;
    private _downloadRequests: {observer: TreeNode, query: DownloadQuery}[];
    private _view: any;
    private _patch: PatchManager;

    constructor(aPatch: PatchManager) {
        super();

        this._freeDownloadSlots = 16;

        this._visiteeConsumerFunction = this.manage.bind(this);
        this._ordering = VisitorOrdering.pre;

        this._downloadRequests = [];

        this._view = null;
        this._patch = aPatch;
    }

    public setView(aView: any) {
        this._view = aView;
    }

    public manage(aVisitee: TreeNode) {
        if (aVisitee.getState() === ChunkLODNodeState.CREATED) // we have new node
        {
            // schedule data request (dispatch ajax request when new download slot will be ready
            aVisitee.setState(ChunkLODNodeState.DATAREQUESTED);
            this._downloadRequests.push({
                observer: aVisitee,
                query: new DownloadQuery([aVisitee.getAddress().getDataUrl()])
            });
        }
    }

    public stopDownload(aVisitee: TreeNode) {
        // find the query in not finished download requests
        let request;
        for (let i = 0; i < this._downloadRequests.length; i++) {
            request = this._downloadRequests[i];
            if (request.observer === aVisitee) {
                if (request.query.getState() === eDownloadQueryState.SCHEDULED) {
                    request.query.abortDownload();
                }
                this._freeDownloadSlots += request.query.getKeepDownloadSlot() ? 1 : 0;
                request.query.setKeepDownloadSlot(false);

                this._downloadRequests.splice(i, 1);
                return;
            }
        }
    }

    public stopDownloads(aNodesToBeRemoved: TreeNode[]) {
        let node;
        for (let j = 0; j < aNodesToBeRemoved.length; j++) {
            node = aNodesToBeRemoved[j];
            if (node.getState() === ChunkLODNodeState.DATAREQUESTED) {
                this.stopDownload(node);
                node.setState(ChunkLODNodeState.CREATED);
            }
        }
    }

    public sortRequests() {
        let priorityA = null;
        let priorityB = null;

        let levelA = 0;
        let levelB = 0;

        this._downloadRequests.sort((a, b): number => {
            priorityA = this._patch.getPriorityManager().getPriority(a.observer);
            priorityB = this._patch.getPriorityManager().getPriority(b.observer);

            levelA = a.observer.getAddress().level;
            levelB = b.observer.getAddress().level;

            // lower level higher priority
            if (levelB != levelA) {
                //return levelA - levelB;
            }

            if (priorityA !== priorityB) {
                return priorityB - priorityA;
            }

            // higher distance, lower priority
            return vec3.distance(this._view.eye, b.observer.getCenter()) - vec3.distance(this._view.eye, a.observer.getCenter());

        });
    }

    public update(aNodesToRemove: TreeNode[]) {
        // first find and remove nodes which was required to be removed
        this.stopDownloads(aNodesToRemove);

        this.sortRequests();

        // process finished or failed requests to acquire free download slots
        for (let i = 0; i < this._downloadRequests.length; i++) {
            let request = this._downloadRequests[i];

            switch (request.query.getState()) {
                case eDownloadQueryState.FINISHED:
                    request.observer.setData(request.query.getData());
                    request.observer.setState(ChunkLODNodeState.DATAREADY);
                    this._freeDownloadSlots += request.query.getKeepDownloadSlot() ? 1 : 0;
                    request.query.setKeepDownloadSlot(false);
                    break;
                case eDownloadQueryState.FAILED:
                    this._freeDownloadSlots += request.query.getKeepDownloadSlot() ? 1 : 0;
                    request.query.setKeepDownloadSlot(false);
                    request.observer.setState(ChunkLODNodeState.SETTLEDONGPU);
                    break;
            }
        }

        // dispatch new requests
        let remainingRequests = [];
        for (let i = 0; i < this._downloadRequests.length; i++) {
            let request = this._downloadRequests[i];

            switch (request.query.getState()) {
                case eDownloadQueryState.NOTSCHEDULED:
                    if (this._freeDownloadSlots > 0) {
                        request.query.setKeepDownloadSlot(true);
                        request.query.download();
                        this._freeDownloadSlots--;
                    }
                    remainingRequests.push(request);
                    break;
                case eDownloadQueryState.SCHEDULED:
                    remainingRequests.push(request);
                    break;
            }
        }

        this._downloadRequests = remainingRequests;
    }
}